import { ReactComponentElement, ReactNode } from 'react';
import Logo from '../../Logo';
import ResponsiveImage, {
  ResponsiveImageProps,
} from '../../ResponsiveImage/ResponsiveImage';
import styles from './mainLayout.module.css';
import global from '../../../global.module.css';
import Footer from '../Footer/Footer';
import LanguageToggle from '../../LanguageToggle';
import StickyQR from '../../StickyQR';
import RefreshButton from '../../Button/RefreshButton/RefreshButton';
import useMobileViewportUnitsFix from './useMobileViewportUnitsFix';

export interface MainLayoutProps extends ResponsiveImageProps {
  children: ReactNode;
  LanguageToggle: ReactComponentElement<typeof LanguageToggle>;
  enableRefresh?: boolean;
  refreshButtonText?: string | ReactNode;
  Footer?: ReactComponentElement<typeof Footer>;
  qRCodeUrl?: string;
  qrCodeTagline?: string;
  BackButton?: JSX.Element;
}

const MainLayout = ({
  imageSrc,
  imageAlt,
  imageSrcMobile,
  children,
  Footer,
  LanguageToggle,
  enableRefresh = false,
  refreshButtonText,
  qRCodeUrl,
  qrCodeTagline,
  dataTestIdPrefix,
  BackButton,
}: MainLayoutProps) => {
  const classes = [
    styles.mainLayout,
    !imageSrcMobile ? styles[`mainLayout--noMobileImage`] : '',
  ].join(' ');
  useMobileViewportUnitsFix();

  return (
    <div className={classes}>
      <section
        className={[styles.mainLayoutHeader, global.contentPadding].join(' ')}
      >
        {BackButton}
        {enableRefresh && <RefreshButton text={refreshButtonText} />}
        {LanguageToggle}
      </section>
      <section className={styles.mainLayoutImage}>
        {qRCodeUrl !== undefined ? (
          <StickyQR
            url={qRCodeUrl}
            className={styles.mainLayoutImageContainer}
            tagline={qrCodeTagline}
          >
            <ResponsiveImage
              imageSrc={imageSrc}
              imageAlt={imageAlt}
              imageSrcMobile={imageSrcMobile}
              type="mainLayout"
              dataTestIdPrefix={dataTestIdPrefix}
            />
          </StickyQR>
        ) : (
          <ResponsiveImage
            imageSrc={imageSrc}
            imageAlt={imageAlt}
            imageSrcMobile={imageSrcMobile}
            type="mainLayout"
            dataTestIdPrefix={dataTestIdPrefix}
          />
        )}
      </section>
      <section className={styles.mainLayoutContent}>
        <section>
          <Logo dataTestIdPrefix={dataTestIdPrefix} />

          {children}
        </section>
        {Footer && (
          <section className={styles.mainLayoutFooter}>{Footer}</section>
        )}
      </section>
    </div>
  );
};

export default MainLayout;
